/**
 * Invoices Page — helpers, constants, and shared types
 */

import type { Address, Invoice, InvoiceFormState } from '../../types/billing'

// ── View mode ────────────────────────────────────────────────────────────────

export type ViewMode = 'list' | 'create' | 'edit' | 'view'

// ── Clone ──────────────────────────────────────────────────────────────────────

/**
 * Builds the seed data for cloning an invoice/bill: it copies all the content
 * (line items, party, addresses, currency, notes, terms, bank, export fields…)
 * but drops everything that identifies the source — number, dates, status,
 * IRN/e-way/ack — so the form saves it as a brand-new draft with a freshly
 * generated number. Fed to the create form as `initialData`.
 */
export function buildCloneSource(invoice: Invoice): Partial<Invoice> {
  const clone: Partial<Invoice> = { ...invoice }
  delete clone.id
  delete clone.invoiceNumber
  delete clone.invoicePrefix
  delete clone.proformaNumber
  delete clone.proformaPrefix
  delete clone.invoiceDate
  delete clone.dueDate
  delete clone.irn
  delete clone.irnDate
  delete clone.ackDate
  delete clone.ewayBillNumber
  delete clone.ewayBillDate
  delete clone.postedAt
  delete clone.paymentStatus
  clone.status = 'draft'
  return clone
}

// ── Page-level vendor-type constant ─────────────────────────────────────────

/** Invoice types that belong to the vendor (bills) category. */
export const VENDOR_TYPES = ['vendor_bill', 'vendor_credit_note'] as const

// ── Page info data ───────────────────────────────────────────────────────────

export const INVOICE_FEATURES = [
  {
    title: 'Draft & Final Invoices',
    description:
      'Create invoices as drafts for review, then post them to finalize and generate journal entries',
  },
  {
    title: 'Line Items with Tax',
    description:
      'Add multiple line items with quantities, rates, discounts, and tax calculations per line',
  },
  {
    title: 'Multi-Currency Support',
    description:
      'Issue invoices in any enabled currency with automatic exchange rate conversion',
  },
  {
    title: 'PDF Generation',
    description:
      'Generate professional PDF invoices with your organization details, ready to send to customers',
  },
]

export const INVOICE_STEPS = [
  {
    title: 'Create an invoice',
    description:
      'Click "New Invoice" to select a customer, add line items, and set payment terms.',
  },
  {
    title: 'Review and post',
    description:
      'Review the draft, then post it to create the corresponding journal entry and update receivables.',
  },
  {
    title: 'Record payments',
    description:
      'When the customer pays, record a payment to allocate against the invoice and track outstanding balances.',
  },
]

export const INVOICE_WHAT_IS_IT = {
  title: 'What are Invoices?',
  description:
    'Invoices are formal requests for payment sent to customers. Each posted invoice creates a journal entry debiting Accounts Receivable and crediting Revenue, keeping your books accurate and up-to-date.',
}

// ── Address sanitizer ─────────────────────────────────────────────────────────

const ADDRESS_FIELDS = [
  'line1', 'line2', 'city', 'state', 'stateCode',
  'postalCode', 'country', 'countryCode', 'gstin', 'companyCode',
] as const

/**
 * Extract only proto-compatible address fields, discarding UI-only fields
 * such as `id`, `label`, `type`, and `isPrimary`.
 */
export function sanitizeAddress(addr: Partial<Address> | undefined): Address | undefined {
  if (!addr) { return undefined }
  const result = {} as Record<typeof ADDRESS_FIELDS[number], string>
  for (const field of ADDRESS_FIELDS) {
    result[field] = addr[field] ?? ''
  }
  return result
}

// ── Date helpers ──────────────────────────────────────────────────────────────

/** Format a date string as DD Mon YYYY for display. */
export function formatDisplayDate(d: string | undefined): string {
  if (!d) { return '-' }
  return new Date(d).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

/** Return today's date as YYYY-MM-DD string. */
export function todayISODate(): string {
  const now = new Date()
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

// ── Invoice form payload builders ─────────────────────────────────────────────

/** Map form line items to proto-compatible payload shape. */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- TODO(refactor): define explicit return type
export function buildItemsPayload(items: InvoiceFormState['items']) {
  return items.map(item => ({
    itemCode: item.itemCode,
    description: item.description,
    hsnCode: item.hsnCode,
    sacCode: item.sacCode,
    quantity: item.quantity,
    unit: item.unit,
    unitPrice: item.unitPrice,
    discountPercent: item.discountPercent,
    cgstRate: item.cgstRate,
    sgstRate: item.sgstRate,
    igstRate: item.igstRate,
  }))
}

/** Extract export and shipping fields from form state. */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- TODO(refactor): define explicit return type
export function buildSharedExportFields(formState: InvoiceFormState) {
  return {
    exportType: formState.exportType,
    countryOfDestination: formState.countryOfDestination,
    countryOfDestinationCode: formState.countryOfDestinationCode,
    shipToAddress: sanitizeAddress(formState.shipToAddress),
    consigneeAddress: sanitizeAddress(formState.consigneeAddress),
    notifyPartyAddress: sanitizeAddress(formState.notifyPartyAddress),
    portOfLoading: formState.portOfLoading,
    portOfDischarge: formState.portOfDischarge,
    modeOfTransport: formState.modeOfTransport,
    vesselFlightNumber: formState.vesselFlightNumber,
    blAwbNumber: formState.blAwbNumber,
    containerNumbers: formState.containerNumbers,
    shippingBillNumber: formState.shippingBillNumber,
    shippingBillDate: formState.shippingBillDate,
  }
}

/** Calculate how many days overdue a due-date is (negative = not yet due). */
export function calcDaysOverdue(dueDate: string | undefined): number {
  if (!dueDate) { return 0 }
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const due = new Date(dueDate.includes('T') ? dueDate : `${dueDate}T00:00:00`)
  due.setHours(0, 0, 0, 0)
  return Math.floor((today.getTime() - due.getTime()) / (1000 * 60 * 60 * 24))
}
